import React, { PropsWithChildren } from "react";

import { Button, SidePanel, Title } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Trans } from "react-i18next";

import { UnsavedChangesModal } from "@/components/organisms/UnsavedChangesModal/UnsavedChangesModal";

import { cn } from "@/utils/cn";

export interface EditPanelProps extends PropsWithChildren {
	isEditing: boolean;
	isLoading?: boolean;
	editTitle?: string;
	viewHeader?: React.ReactNode;
	/** The header's own close (×), view mode only. */
	onClose: () => void;
	/**
	 * The panel dismissed from outside its header - Escape, the backdrop
	 * (the mobile modal). Defaults to `onClose`.
	 */
	onDismiss?: () => void;
	onCancel: () => void;
	onSave: () => void;
	showConfirmModal: boolean;
	onDiscard: () => void;
	onKeepEditing: () => void;
}

/**
 * The lib's SidePanel around a view/edit form: a sticky column on
 * desktop, a centered modal under 1280px (`mobileMode="modal"` - a task
 * form is not something to peek at from a docked sheet). The header
 * (title or view header, Cancel/Save or ×) goes in the SidePanel's own
 * `header` slot - the row the lib keeps put while the body scrolls -
 * rather than a sticky element inside its padded body, which left a gap
 * above it for the content to show through. The buttons drive the form
 * through its ref (usePanelState), so nothing here needs Formik's
 * context.
 */
export const EditPanel: React.FC<EditPanelProps> = ({
	isEditing,
	isLoading = false,
	editTitle,
	viewHeader,
	onClose,
	onDismiss = onClose,
	onCancel,
	onSave,
	showConfirmModal,
	onDiscard,
	onKeepEditing,
	children,
}) => {
	const header = (
		<div className="flex w-full items-center justify-between">
			{isEditing ? (
				<Title size="h3">{editTitle}</Title>
			) : (
				(viewHeader ?? null)
			)}
			<div
				className={cn(["flex gap-2", !isEditing && "mt-1 self-start"])}
			>
				{isEditing ? (
					<>
						<Button
							key="cancel"
							size="sm"
							color="white"
							className="text-neutral-600!"
							onClick={onCancel}
							isLoading={isLoading}
						>
							{t("actions.cancel")}
						</Button>
						<Button
							key="save"
							size="sm"
							onClick={onSave}
							isLoading={isLoading}
						>
							{t("actions.save")}
						</Button>
					</>
				) : (
					<Button
						size="sm"
						color="white"
						className="text-neutral-600!"
						onClick={onClose}
						picto="cross"
					/>
				)}
			</div>
		</div>
	);

	return (
		<SidePanel
			open
			onOpenChange={(next) => {
				if (!next) onDismiss();
			}}
			header={header}
			hideCloseButton
			mobileMode="modal"
			portal
			cardProps={{
				shadow: {
					blur: 2,
					x: 0,
					y: 0,
				},
			}}
		>
			{children}

			<UnsavedChangesModal
				open={showConfirmModal}
				onClose={onKeepEditing}
				onConfirm={onDiscard}
			>
				<Trans i18nKey="edit.unsavedChanges.message" />
			</UnsavedChangesModal>
		</SidePanel>
	);
};
